import React from "react";
import { X } from "lucide-react";

import { TextInput } from "@/shared/components/settings-atoms";
import { IconButton } from "@/shared/components/ui-kit";
import { CHANNELS, STAGES } from "./copy";
import { ToggleChip } from "./ToggleChip";
import { TokenList } from "./TokenList";
import { useSavedText } from "./useSavedText";

import type { RuleWhen } from "../../types";

export const CONDITIONS: Record<keyof RuleWhen, { label: string; hint?: string }> = {
  channels: { label: "Channel is", hint: "Matches any channel you pick." },
  people: { label: "Email or domain", hint: "An email matches one person; @acme.com matches everyone at acme.com. Press Enter to add." },
  answer: { label: "Onboarding answer" },
  status: { label: "Onboarding stage" },
};

/** A question of the saved flow an answer can be matched on. */
export interface FlowQuestion {
  key: string;
  label: string;
}

/* The conditions of a policy, one row each, every one of which must hold: channel chips, the
   email-or-domain tokens, a question of the saved flow and what its answer includes, the
   onboarding stage. Each row has its ×. Changes go up at once; the answer's words when the
   field is left. */
export function Conditions({ when, questions, onChange }: {
  when: RuleWhen;
  questions: FlowQuestion[];
  onChange: (when: RuleWhen) => void;
}) {
  const remove = (key: keyof RuleWhen) => {
    const next = { ...when };
    delete next[key];
    onChange(next);
  };
  const flip = <T extends string>(list: T[], value: T) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  return (
    <div className="rounded-[12px] ring-1 ring-[var(--hairline)] px-3 divide-y divide-[var(--hairline)]">
      {when.channels && (
        <Condition kind="channels" onRemove={() => remove("channels")}>
          <Chips
            options={CHANNELS}
            isOn={(id) => when.channels!.includes(id)}
            onFlip={(id) => onChange({ ...when, channels: flip(when.channels!, id) })}
          />
        </Condition>
      )}
      {when.people && (
        <Condition kind="people" onRemove={() => remove("people")}>
          <TokenList label="Add an email or domain" items={when.people} onChange={(people) => onChange({ ...when, people })} />
        </Condition>
      )}
      {when.answer && (
        <Condition kind="answer" onRemove={() => remove("answer")} hint={questions.length ? undefined : "The flow asks no questions yet."}>
          <Chips
            options={questions.map((q) => [q.key, q.label] as [string, string])}
            isOn={(key) => when.answer!.key === key}
            onFlip={(key) => onChange({ ...when, answer: { ...when.answer!, key } })}
          />
          {when.answer.key && (
            <AnswerValue value={when.answer.value} onSave={(value) => onChange({ ...when, answer: { ...when.answer!, value } })} />
          )}
        </Condition>
      )}
      {when.status && (
        <Condition kind="status" onRemove={() => remove("status")}>
          <Chips
            options={STAGES}
            isOn={(id) => when.status!.includes(id)}
            onFlip={(id) => onChange({ ...when, status: flip(when.status!, id) })}
          />
        </Condition>
      )}
    </div>
  );
}

function Condition({ kind, hint = CONDITIONS[kind].hint, onRemove, children }: {
  kind: keyof RuleWhen;
  hint?: string;
  onRemove: () => void;
  children: React.ReactNode;
}) {
  const { label } = CONDITIONS[kind];
  return (
    <div role="group" aria-label={label} className="py-3 flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <span className="flex-1 text-[13.5px] font-medium text-[var(--text-primary)]">{label}</span>
        <IconButton type="button" aria-label={`Remove ${label}`} onClick={onRemove}>
          <X className="w-[14px] h-[14px]" strokeWidth={1.75} />
        </IconButton>
      </div>
      {children}
      {hint && <p className="m-0 text-[12px] leading-[1.55] text-[var(--text-tertiary)]">{hint}</p>}
    </div>
  );
}

function Chips<T extends string>({ options, isOn, onFlip }: {
  options: [T, string][];
  isOn: (id: T) => boolean;
  onFlip: (id: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(([id, name]) => (
        <ToggleChip key={id} on={isOn(id)} onClick={() => onFlip(id)}>{name}</ToggleChip>
      ))}
    </div>
  );
}

function AnswerValue({ value, onSave }: { value: string; onSave: (value: string) => void }) {
  const text = useSavedText(value, onSave);
  return (
    <TextInput
      aria-label="Their answer includes"
      placeholder="Their answer includes…, e.g. Alerts"
      value={text.value}
      onChange={text.set}
      onBlur={text.onBlur}
      onKeyDown={text.onKeyDown}
    />
  );
}
